import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { ImageType } from '../../../enums';
import { useHasAccess } from '../../../hooks/useHasAccess';
import { Event } from '../../../interfaces';
import { basic } from '../../../theme/colors';
import { getEventImageType, isContextCamera } from '../../../utils';
import { calculateContextCameraCoordinates, calculatePoint, getLicensePlateBounds } from '../../../utils/homographyHelpers';

// Lightweight alternative to ZoomableImage for the inline thumbnail path.
// Renders a plain <img> with an SVG overlay sized to the image's natural
// dimensions. Long review sessions previously kept one full-resolution canvas
// per visible thumb alive on the GPU; this path holds none.
//
// Supported overlays mirror the inline-grid subset of ZoomableImage: red dot,
// pink dot (admin), and LPR POV polygon. Zoom and the heavier metadata
// overlays (lanes, detections, road edges, plate detection) still go through
// ZoomableImage in the modal path.

const Wrapper = styled.div`
  position: relative;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  max-width: 100%;
  max-height: 100%;
  background: ${basic.haydenGreyBackground};
`;

const Image = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const Overlay = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

export interface ImageWithSvgOverlaysProps {
  src: string | null;
  event?: Event;
  drawLprPov?: boolean;
  width?: number | string;
  height?: number | string;
  loading?: 'lazy' | 'eager';
  ariaLabel?: string;
  'data-accesskey'?: string;
  onContextMenu?: (e: React.SyntheticEvent) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

// Mirrors ZoomableImage.drawRedDot: prefer a typed plate_crop entry, fall back
// to the context-camera bounds midpoint (offset above the plate), then to the
// inverse-homography midpoint.
function computeRedDotCenter(event: Event, hasAccess: boolean, radius: number): { x: number; y: number } | null {
  const imageType = getEventImageType(event, hasAccess);
  const typed = event.plate_crops?.find(
    (pc) => pc.image_type === imageType || (imageType === ImageType.PreContext && pc.image_type === ImageType.PreContextRedDot),
  );
  if (typed) {
    const crop = typed.plate_crop;
    if (crop?.length === 2) {
      return { x: crop[0], y: crop[1] };
    }
    return null;
  }

  if (isContextCamera(event)) {
    if (!event.plate_crop) {
      return null;
    }
    const bounds = getLicensePlateBounds(event.plate_crop);
    if (bounds.length < 4) {
      return null;
    }
    const x = (bounds[0].x + bounds[2].x) / 2;
    const y = (bounds[0].y + bounds[3].y) / 2 - 4 * radius;
    return { x, y };
  }

  if (event.metadata?.homography && event.plate_crop) {
    return calculatePoint(event.metadata.homography, event.plate_crop);
  }

  return null;
}

interface OverlaysProps {
  event: Event;
  naturalWidth: number;
  hasAccess: boolean;
  drawLprPov?: boolean;
}

function Overlays(props: OverlaysProps) {
  const { event, naturalWidth, hasAccess, drawLprPov } = props;

  // Marker radius in viewBox (= image-natural) units. Visual size scales with
  // the displayed image: ~display_px / 120. For a 600px-wide thumb that's a
  // 5px dot, matching the canvas implementation closely enough.
  const r = naturalWidth / 120;

  const redDot = computeRedDotCenter(event, hasAccess, r);
  const pinkDot = hasAccess ? event.metadata?.car_detection?.estimated_plate_location : null;
  const pov = drawLprPov && event.metadata?.homography ? calculateContextCameraCoordinates(event.metadata.homography, event) : null;

  return (
    <>
      {redDot && <circle cx={redDot.x} cy={redDot.y} r={r} fill="red" />}
      {pinkDot && pinkDot.length === 2 && <circle cx={pinkDot[0]} cy={pinkDot[1]} r={r} fill="pink" />}
      {pov && pov.length > 0 && (
        <polygon
          points={pov.map(([x, y]) => `${x},${y}`).join(' ')}
          fill="none"
          stroke="blue"
          strokeWidth={3}
          vectorEffect="non-scaling-stroke"
        />
      )}
    </>
  );
}

export function ImageWithSvgOverlays(props: ImageWithSvgOverlaysProps) {
  const { src, event } = props;
  const hasAccess = useHasAccess();
  const [dimensions, setDimensions] = useState<{ w: number; h: number } | null>(null);

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      setDimensions({ w: img.naturalWidth, h: img.naturalHeight });
    }
  };

  useEffect(() => {
    setDimensions(null);
  }, [src]);

  return (
    <Wrapper
      style={{ width: props.width, height: props.height }}
      data-test="zoomable-image"
      data-accesskey={props['data-accesskey']}
      onContextMenu={props.onContextMenu}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
      aria-hidden={!props.ariaLabel}
    >
      {src && (
        <Image
          src={src}
          loading={props.loading ?? 'lazy'}
          decoding="async"
          onLoad={handleLoad}
          alt={props.ariaLabel ?? ''}
        />
      )}
      {dimensions && event && (
        <Overlay viewBox={`0 0 ${dimensions.w} ${dimensions.h}`} preserveAspectRatio="xMidYMid meet">
          <Overlays event={event} naturalWidth={dimensions.w} hasAccess={hasAccess} drawLprPov={props.drawLprPov} />
        </Overlay>
      )}
    </Wrapper>
  );
}
